import { Box } from '@primer/react';
import {
  AccessLevel,
  isEnoughAccessToReadOutput,
  isEnoughAccessToReadWithoutWeights,
} from '@upright/common/iam/access/types';
import { SpecialDataSourceId } from '@upright/common/model/input/data-sources/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { ModelEntityType } from '@upright/common/model/input/types';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { Valence } from '@upright/common/model/types';
import { friendlyShortImpactTitle, friendlyValence } from '@upright/common/utils';
import { Link } from '@upright/ui/common/generic/components/primer-custom/link';
import { formatImpactScore } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/value-formatter';
import LightBulbIcon from '@upright/ui/icons/img/manual/Lightbulb-On--Streamline-Core.svg';
import TrendDownIcon from '@upright/ui/icons/img/manual/TrendDown.svg';
import TrendUpIcon from '@upright/ui/icons/img/manual/TrendUp.svg';
import { getModelEntityUrl } from '@upright/ui/untidy/urls';
import { isNil, lowerCase } from 'lodash';
import { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { useNetImpactLensContext } from '../../../common/use-net-impact-lens-context';
import { formatPercentileIntoTopPercentByValence } from '../../../common/utils';
import { ValencedSubHeaderWithIcon } from './valenced-subheader-with-icon';
import { FundBenchmarkUniverseCount } from '@upright/ui/views/model-entity-views/groupfolio-view/histogram-block/summary';

type Props = {
  valence: Valence.NEGATIVE | Valence.POSITIVE;
  entityType: ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;
  largest:
    | {
        impactCode: ImpactCategoryId;
        topContributors:
          | Array<{
              id: string;
              name: string;
              type: ModelEntityType;
              accessLevel: AccessLevel | null | undefined;
              topContributors:
                | Array<{
                    name: string;
                    id: string;
                    type: ModelEntityType;
                  }>
                | undefined
                | 'loading';
            }>
          | undefined;
      }
    | undefined;
  mostDistinct:
    | {
        impactCode: ImpactCategoryId;
        percentile: number;
      }
    | undefined;
  disableLinks: boolean | undefined;
  accessLevel?: AccessLevel;
  source?: string;
};

export function TopImpactContent(props: Props) {
  const valenceText = lowerCase(friendlyValence(props.valence));
  const getFormattedScore = useGetFormattedScore(props.valence);

  const showHoldings = (() => {
    const accessLevel = props.accessLevel;
    const source = props.source;

    if (isEnoughAccessToReadWithoutWeights(accessLevel)) {
      return true;
    }
    if (isEnoughAccessToReadOutput(accessLevel) && source === SpecialDataSourceId.FINNHUB) {
      return true;
    }
    return false;
  })();

  return (
    <div>
      <Section>
        <TopLargestPart
          data={props.largest}
          valence={props.valence}
          getFormattedScore={getFormattedScore}
          disableLinks={props.disableLinks ?? false}
          showHoldings={showHoldings}
        />
      </Section>
      <Section>
        <ValencedSubHeaderWithIcon
          icon={props.valence === Valence.POSITIVE ? TrendUpIcon : TrendDownIcon}
          valence={props.valence}
        >
          Most distinct {valenceText} impact
        </ValencedSubHeaderWithIcon>
        <TopDistinctPart data={props.mostDistinct} valence={props.valence} getFormattedScore={getFormattedScore} />
      </Section>
    </div>
  );
}

/**
 * Handle digging up the scores themselves from the lens context, provide a readily formatted function to get them.
 */
function useGetFormattedScore(defaultValence: Valence.NEGATIVE | Valence.POSITIVE) {
  const context = useNetImpactLensContext();
  const { formatters, scoreUnit, scoreReader } = context?.options ?? {};
  const showFriendlyShortUnit = context?.options.scoreUnit === FinalImpactScoreType.CENTS_PER_DOLLAR;
  return (impact: ImpactCategoryId, valence: Valence.NEGATIVE | Valence.POSITIVE = defaultValence) => {
    const value = scoreReader?.readScore(impact, undefined, undefined, valence);
    if (isNil(value) || isNil(formatters) || isNil(scoreUnit)) {
      return undefined;
    }
    return `${formatImpactScore(value, formatters, scoreUnit)}${showFriendlyShortUnit ? ' ¢/$' : ''}`;
  };
}

const Section = styled.div`
  &:not(:last-child) {
    padding-bottom: 6px;
  }
`;

function getValenceText(valence: Valence.NEGATIVE | Valence.POSITIVE) {
  return lowerCase(friendlyValence(valence));
}

function TopLargestPart(props: {
  showHoldings: boolean;
  valence: Valence.NEGATIVE | Valence.POSITIVE;
  data:
    | {
        impactCode: ImpactCategoryId;
        topContributors:
          | Array<{
              id: string;
              name: string;
              type: ModelEntityType;
              accessLevel: AccessLevel | null | undefined;
              topContributors:
                | Array<{
                    name: string;
                    id: string;
                    type: ModelEntityType;
                  }>
                | undefined
                | 'loading';
            }>
          | undefined;
      }
    | undefined;
  getFormattedScore: (impactCode: ImpactCategoryId) => ReactNode;
  disableLinks: boolean;
}) {
  if (!props.data) {
    return <Skeleton />;
  }
  const valenceText = getValenceText(props.valence);

  return (
    <>
      <ValencedSubHeaderWithIcon icon={LightBulbIcon} valence={props.valence}>
        Largest {valenceText} impact
      </ValencedSubHeaderWithIcon>
      <Box sx={{ color: 'text.muted', my: '4px' }}>
        <strong>{friendlyShortImpactTitle(props.data.impactCode)}</strong> (
        {props.getFormattedScore(props.data.impactCode)})
      </Box>
      {props.showHoldings && (
        <TopContributorDescriptor topContributors={props.data.topContributors} disableLinks={props.disableLinks} />
      )}
    </>
  );
}

function TopDistinctPart(props: {
  valence: Valence.NEGATIVE | Valence.POSITIVE;
  data:
    | {
        impactCode: ImpactCategoryId;
        percentile: number;
      }
    | undefined;
  getFormattedScore: (impactCode: ImpactCategoryId) => ReactNode;
}) {
  if (!props.data) {
    return <Skeleton />;
  }
  return (
    <Box sx={{ color: 'text.muted' }}>
      <Box sx={{ my: '4px' }}>
        <strong>{friendlyShortImpactTitle(props.data.impactCode)}</strong> (
        {props.getFormattedScore(props.data.impactCode)})
      </Box>
      <p>
        Out of the <FundBenchmarkUniverseCount /> funds in the Upright database,{' '}
        <strong>
          {formatPercentileIntoTopPercentByValence(props.data.percentile, props.valence)} have a{' '}
          {props.valence === Valence.NEGATIVE ? 'lower negative' : 'higher positive'} impact
        </strong>{' '}
        in this category.
      </p>
    </Box>
  );
}

function TopContributorDescriptor(props: {
  topContributors:
    | Array<{
        id: string;
        name: string;
        type: ModelEntityType;
        accessLevel: AccessLevel | null | undefined;
        topContributors:
          | Array<{
              name: string;
              id: string;
              type: ModelEntityType;
            }>
          | undefined
          | 'loading';
      }>
    | undefined;
  disableLinks: boolean;
}) {
  if (!props.topContributors) {
    return <Skeleton />; // undefined == still loading
  }
  if (!props.topContributors.length) {
    return null; // empty array == loaded but nothing to show
  }
  const topContributor = props.topContributors[0];
  const subContributors = topContributor.topContributors;
  return (
    <Box sx={{ color: 'text.muted' }}>
      Driven by {friendlyContributorType(topContributor.type)} such as{' '}
      {(() => {
        const linkContent = <strong>{topContributor.name}</strong>;
        if (props.disableLinks || !isEnoughAccessToReadOutput(topContributor.accessLevel)) {
          return linkContent; // Might be useful in render view, I dunno
        } else {
          return (
            <Link
              indicator={'underline'}
              to={getModelEntityUrl(topContributor.type, topContributor.id, topContributor.name)}
            >
              {linkContent}
            </Link>
          );
        }
      })()}
      <SubContributorDescriptor subContributors={subContributors} />
    </Box>
  );
}

function SubContributorDescriptor(props: {
  subContributors:
    | Array<{
        name: string;
        id: string;
        type: ModelEntityType;
      }>
    | undefined
    | 'loading';
}) {
  if (props.subContributors === 'loading') {
    return <Skeleton />;
  }
  if (!props.subContributors || !props.subContributors.length) {
    return null;
  }
  // TODO: Should properly filter that all subcontributors are of same entityType, so we don't list e.g. "Mcdonalds and S&P500 as companies"
  const entityType = props.subContributors[0].type;
  return (
    <span>
      {' '}
      and their {friendlyContributorType(entityType)}:
      <Box as="ul" sx={{ listStylePosition: 'inside' }}>
        {props.subContributors.map(c => (
          <li key={c.id}>{c.name}</li>
        ))}
      </Box>
    </span>
  );
}

function friendlyContributorType(type: ModelEntityType) {
  switch (type) {
    case ModelEntityType.COMPANY:
      return 'companies';
    case ModelEntityType.GROUP:
    case ModelEntityType.PORTFOLIO:
      return 'holdings';
    case ModelEntityType.PRODUCT:
    case ModelEntityType.PHENOMENON:
    case ModelEntityType.MIXIN:
      return 'products';
  }
}
